import {Component, DoCheck, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {ComponentSource} from "../../../models/component-source";
import {DeleteToFromCapacity, TransportOrder} from "../../../../pb-screen/models/transport-order/transport-order.interface";
import {PlanningBlock} from "../../../../pb-screen/models/planning-block/planning-block.interface";
import {SelectionType, SelectionTypes} from "../../../../static/selection-types";
import {ButtonVisibilityService} from "../../../services/button-visibility.service";
import {ButtonsIDEnum} from "../../../models/buttons-visibility";
import {OrderLine} from "../../../../pb-screen/models/order-line/orderLine.interface";
import {TranslateService} from "@ngx-translate/core";
import {select} from "@angular-redux/store";
import {IConnectionStatus} from "../../../../store/IAppState";
import {takeUntil} from "rxjs/operators";
import {Subject, Observable} from "rxjs";


@Component({
  selector: "planningactions-buttongroup",
  templateUrl: "./planningactions-buttongroup.component.html",
  styleUrls: ["./planningactions-buttongroup.component.scss"]
})
export class PlanningactionsButtongroupComponent implements OnInit, OnDestroy, DoCheck {

  @select('connectionStatus') connectionStatus$: Observable<IConnectionStatus>;
  private isStale: boolean = false;
  private destroyed$ = new Subject<void>();

  // Dropdown open/hover/click state management
  public isOpen = false;
  private isHoveringGroup = false;
  private clickedStickyOpen = false;
  private suppressHoverOpenUntilLeave = false;
  private suppressInitialHoverUntilMove = false;
  private hoverCloseTimeout?: any;
  private wasComponentVisible = false;

  constructor(private readonly buttonsVisibilityService: ButtonVisibilityService,
              private readonly translateService: TranslateService) {
  }

  @Output() refreshRequested =
    new EventEmitter<{
      source: "" | string,
      transportOrders?: [] | TransportOrder[],
      planningBlocks?: [] | PlanningBlock[]
      deletedCapacity?: [] | DeleteToFromCapacity[]
    }>();

  @Input() selectionType: SelectionType = SelectionTypes.TransportOrder;
  @Input() selectedOrderLines?: OrderLine[];
  @Input() selectedPlanningBlocks?: PlanningBlock[];
  @Input() selectedTransportOrders?: TransportOrder[];
  // Optional hook invoked right before executing any confirmed action
  @Input() onStart?: () => void;
  // Disable state and tooltip support
  @Input() disabled: boolean = false;
  @Input() disabledTooltip?: string;

  protected readonly componentSource = ComponentSource;
  protected readonly ButtonsIDEnum = ButtonsIDEnum;
  protected readonly SelectionTypes = SelectionTypes;

  translate(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }

  ngOnInit() {
    this.connectionStatus$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(status => {
        this.isStale = status ? status.isDataStale : false;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngDoCheck(): void {
    const isCurrentlyVisible = this.isComponentVisible();
    if (isCurrentlyVisible !== this.wasComponentVisible) {
      this.wasComponentVisible = isCurrentlyVisible;
      if (isCurrentlyVisible) {
        this.resetMenuState();
        this.suppressInitialHoverUntilMove = true;
      } else {
        this.resetMenuState();
      }
    }
  }

  private isConnectionStale(): boolean {
    return this.isStale;
  }
  // Handle clicks outside to close when sticky-open by click
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.disabled || this.isStale) {
      return;
    }
    if (this.clickedStickyOpen) {
      this.clickedStickyOpen = false;
      this.evaluateOpenState();
    }
  }

  private evaluateOpenState(): void {
    const shouldBeOpen = !this.disabled &&
      !this.isStale &&
      (this.clickedStickyOpen ||
        (this.isHoveringGroup &&
          !this.suppressHoverOpenUntilLeave &&
          !this.suppressInitialHoverUntilMove));
    this.isOpen = shouldBeOpen;
  }

  private resetMenuState(): void {
    if (this.hoverCloseTimeout) {
      clearTimeout(this.hoverCloseTimeout);
      this.hoverCloseTimeout = undefined;
    }
    this.clickedStickyOpen = false;
    this.isHoveringGroup = false;
    this.isOpen = false;
  }

  public closeMenu(): void {
    this.resetMenuState();
    this.suppressHoverOpenUntilLeave = true;
  }

  public onGroupMouseEnter(): void {
    if (this.disabled || this.isStale) {
      return;
    }
    this.isHoveringGroup = true;
    if (this.hoverCloseTimeout) {
      clearTimeout(this.hoverCloseTimeout);
      this.hoverCloseTimeout = undefined;
    }
    this.evaluateOpenState();
  }

  public onGroupMouseMove(): void {
    if (this.disabled || this.isStale || !this.isHoveringGroup) {
      return;
    }
    if (this.suppressInitialHoverUntilMove) {
      this.suppressInitialHoverUntilMove = false;
      this.evaluateOpenState();
    }
  }

  public onGroupMouseLeave(): void {
    if (this.disabled) {
      return;
    }
    this.isHoveringGroup = false;
    this.suppressHoverOpenUntilLeave = false;
    this.suppressInitialHoverUntilMove = false;
    this.hoverCloseTimeout = setTimeout(() => {
      this.hoverCloseTimeout = undefined;
      this.evaluateOpenState();
    }, 120);
  }

  public onButtonClick(event: MouseEvent): void {
    if (this.disabled || this.isStale) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // Toggle sticky click-open
    this.clickedStickyOpen = !this.clickedStickyOpen;
    if (this.clickedStickyOpen) {
      this.suppressHoverOpenUntilLeave = false;
      this.suppressInitialHoverUntilMove = false;
    }
    this.evaluateOpenState();
  }

  public isComponentVisible(): boolean {
    if (this.selectionType === SelectionTypes.TransportOrder) {
      return !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.SEND_TO) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.CANCEL_TO) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.UNPLAN_TO) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.RESEND_TO) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.SEND_PB);
    }
    if (this.selectionType === SelectionTypes.PlanningBlock || this.selectionType === SelectionTypes.OrderLine) {
      return !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.SEND_PB) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.SEND_TO) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.CANCEL_PB) ||
        !this.buttonsVisibilityService.isButtonHidden(ButtonsIDEnum.UNPLAN_PB);
    }
    return false;
  }

  public isButtonVisible(buttonId: string): boolean {
    return !this.buttonsVisibilityService.isButtonHidden(buttonId);
  }
}
